/**
 * Regenera links Bold en edición (pago único o fila parcial), sobrescribiendo transaction_id.
 */
import axiosInstance from '../../api/axiosInstance'
import { getBoldOrderAmountValidationError } from '../../constants/boldPayments'
import { createBoldPaymentLink, getBoldLinkDetails } from './boldService'
import { updateOrderStatus } from '../orders/updateOrderStatusService'
import {
  buildPagosPatchPayload,
  extractPagosFromOrderDetail
} from './boldPartialLinksAfterOrderSave'

const BOLD_LINK_DEBOUNCE_MS = 2500
const BOLD_REFERENCE_COUNTERS_KEY = 'bold_reference_counters_v1'

function getNextReference(baseReference: string): string {
  const counters = readReferenceCounters()
  const current = Number(counters[baseReference] || 0)
  const next = current + 1
  counters[baseReference] = next
  localStorage.setItem(BOLD_REFERENCE_COUNTERS_KEY, JSON.stringify(counters))
  return `${baseReference}~L${next}`
}

function readReferenceCounters(): Record<string, number> {
  try {
    const raw = localStorage.getItem(BOLD_REFERENCE_COUNTERS_KEY)
    if (!raw) return {}
    const parsed = JSON.parse(raw) as Record<string, unknown>
    const out: Record<string, number> = {}
    Object.entries(parsed).forEach(([k, v]) => {
      const num = Number(v)
      if (!Number.isNaN(num) && num >= 0) out[k] = num
    })
    return out
  } catch {
    return {}
  }
}

export async function regenerateSingleBoldLinkForOrder(params: {
  orderId: number
  totalAmount: number
  storeName: string
  status: string
  paymentMethod?: string
}): Promise<{ ok: boolean; transactionId?: string; url?: string; error?: string }> {
  const { orderId, totalAmount, storeName, status, paymentMethod = 'bold' } = params

  const amountError = getBoldOrderAmountValidationError(totalAmount)
  if (amountError) {
    return { ok: false, error: amountError }
  }

  const linkResult = await createBoldPaymentLink({
    totalAmount: Math.round(totalAmount),
    reference: getNextReference(`ped~${orderId}`),
    description: `Pago pedido ${orderId}`,
    store_name: storeName
  })

  if (!linkResult.success || !linkResult.payment_link) {
    return {
      ok: false,
      error: linkResult.error || 'No se pudo generar el link de pago Bold'
    }
  }

  await new Promise((resolve) => setTimeout(resolve, BOLD_LINK_DEBOUNCE_MS))

  const linkDetails = await getBoldLinkDetails(linkResult.payment_link, storeName)
  const transactionIdToSave =
    linkDetails.success && linkDetails.transaction_id
      ? linkDetails.transaction_id
      : linkResult.payment_link

  try {
    await updateOrderStatus(orderId, {
      status: status || 'verify_transaction',
      meta_data: [
        { key: 'transaction_id', value: transactionIdToSave },
        { key: 'payment_method', value: paymentMethod }
      ]
    })
  } catch (patchErr: unknown) {
    const patchMessage =
      patchErr && typeof patchErr === 'object' && 'response' in patchErr
        ? String(
            (patchErr as { response?: { data?: { message?: string; error?: string } } }).response
              ?.data?.message ||
              (patchErr as { response?: { data?: { error?: string } } }).response?.data?.error ||
              ''
          )
        : 'El enlace se generó pero no se pudo guardar en el pedido.'
    return {
      ok: false,
      error: patchMessage || 'El enlace se generó pero no se pudo guardar en el pedido.',
      transactionId: transactionIdToSave,
      url: linkResult.url || linkResult.payment_link
    }
  }

  return {
    ok: true,
    transactionId: transactionIdToSave,
    url: linkResult.url || linkResult.payment_link
  }
}

export async function regenerateBoldLinkForPartialRow(params: {
  orderId: number
  rowIndex: number
  orderDetail?: Record<string, unknown>
  /** Valor actual del abono en el formulario (si no se guardó el pedido aún) */
  totalAmount?: number
  /** Abonos actuales del formulario (part / plan_separe); evita pisar otros valores al PATCH */
  pagosFromForm?: Array<Record<string, unknown>>
}): Promise<{ ok: boolean; transactionId?: string; url?: string; error?: string }> {
  const {
    orderId,
    rowIndex,
    orderDetail: prefetched,
    totalAmount: totalAmountOverride,
    pagosFromForm
  } = params
  const token = localStorage.getItem('accessToken')
  if (!token) {
    return { ok: false, error: 'No hay sesión' }
  }

  let detail = prefetched
  if (!detail) {
    const res = await axiosInstance.get(`/list/${orderId}`, {
      headers: { Authorization: `Bearer ${token}` }
    })
    detail = res.data as Record<string, unknown>
  }

  const pagosRaw = extractPagosFromOrderDetail(detail)
  const pagosSource =
    pagosFromForm && pagosFromForm.length > 0 ? pagosFromForm : pagosRaw
  if (rowIndex < 0 || rowIndex >= pagosSource.length) {
    return { ok: false, error: 'Fila de pago no encontrada' }
  }

  const pagos = JSON.parse(JSON.stringify(pagosSource)) as Array<Record<string, unknown>>
  const row = pagos[rowIndex]
  const metodo = String(row.metodo_pago ?? '').trim().toLowerCase()
  if (metodo !== 'bold') {
    return { ok: false, error: 'La fila no es un pago Bold' }
  }

  if (Number(row.pago_verificado) === 1) {
    return { ok: false, error: 'No se puede regenerar un pago ya verificado' }
  }

  const totalAmount =
    totalAmountOverride != null
      ? Math.round(totalAmountOverride)
      : Math.round(Number(row.valor) || 0)
  if (totalAmountOverride != null) {
    row.valor = totalAmount
  }
  const amountError = getBoldOrderAmountValidationError(totalAmount)
  if (amountError) {
    return { ok: false, error: amountError }
  }

  const storeName = String(
    detail.store_name ?? (detail.extraDetails as Record<string, unknown> | undefined)?.store_name ?? ''
  ).trim()
  const status = String(detail.status ?? 'verify_transaction')
  const paymentMethod = String(
    detail.payment_method ??
      (detail.extraDetails as Record<string, unknown> | undefined)?.payment_method ??
      'part'
  )

  const linkResult = await createBoldPaymentLink({
    totalAmount,
    reference: getNextReference(`ped~${orderId}`),
    description: `Pago parcial ${rowIndex + 1} — pedido ${orderId}`,
    store_name: storeName
  })

  if (!linkResult.success || !linkResult.payment_link) {
    return {
      ok: false,
      error: linkResult.error || 'No se pudo generar el link Bold'
    }
  }

  await new Promise((r) => setTimeout(r, BOLD_LINK_DEBOUNCE_MS))

  const linkDetails = await getBoldLinkDetails(linkResult.payment_link, storeName)
  const transactionIdToSave =
    linkDetails.success && linkDetails.transaction_id
      ? linkDetails.transaction_id
      : linkResult.payment_link

  row.id_transaccion = transactionIdToSave

  const extraDetails = {
    ...((detail.extraDetails as Record<string, unknown>) || {})
  }
  delete extraDetails.pagos

  const lineItems = (detail.lineItems as unknown[]) || (detail.line_items as unknown[]) || []
  const old_data_order = {
    status: String(detail.status ?? ''),
    customer_note: String(extraDetails.customer_note ?? ''),
    line_items: lineItems,
    transaction_id: String(extraDetails.transaction_id ?? '').trim()
  }

  const pagosPayload = buildPagosPatchPayload(pagos, paymentMethod)

  try {
    await axiosInstance.patch(
      `/list/${orderId}`,
      {
        status,
        payment_method: paymentMethod,
        old_data_order,
        pagos: pagosPayload,
        extraDetails
      },
      {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    )
  } catch (e: unknown) {
    const msg =
      e && typeof e === 'object' && 'response' in e
        ? String((e as { response?: { data?: { message?: string } } }).response?.data?.message || '')
        : ''
    const fallback =
      e && typeof e === 'object' && 'message' in e ? String((e as { message: string }).message) : ''
    return {
      ok: false,
      error: msg || fallback || 'Error al guardar el nuevo link Bold',
      transactionId: transactionIdToSave,
      url: linkResult.url || linkResult.payment_link
    }
  }

  return {
    ok: true,
    transactionId: transactionIdToSave,
    url: linkResult.url || linkResult.payment_link
  }
}
